import {DataTableColumn} from 'mantine-datatable';
import {NavLink} from 'react-router-dom';
import {I18n} from 'aws-amplify/utils';
import {
    Badge,
    Text,
} from '@mantine/core';

import {DnsZone} from '../types';
import {GroupedZone} from '../zoneGrouping';
import {ZoneConnectionNames} from '../connectionNames';
import {GroupedDelegationBadge} from '../components/GroupedDelegationBadge';
import {GroupedRecordCountCell} from '../components/GroupedRecordCountCell';
import {NameserverCount} from '../components/NameserverCount';
import {ZoneConnections} from '../components/ZoneConnections';
import {
    providerColor,
    providerLabel,
} from '../providerLabels';
import config from '../config';

// ⚠️ PROVIDER IS A COLUMN, NOT A GROUPING.
//
// A portfolio spread across three providers is ONE list with provenance as an
// attribute — not three tables, not a grouped table, not a provider switcher.
// That is the point of the neutral layer: `provider` is the only field in the
// schema that reveals where a zone came from, and it earns a column rather than
// restructuring the page.
//
// ─── FNP-1378 (REOPENED): A ROW IS A REAL ZONE, NOT A (ZONE, CONNECTION) PAIR ───
//
// The table's record type is `GroupedZone`. The fan-out still returns one row
// per connection and still returns the union — nothing about the read changed —
// but the table now renders one row per real zone, with every connection that
// reaches it named on that row.
//
// ⚠️ EVERY ACCESSOR IS A PATH INTO THE GROUP, and the paths matter: mantine-
// datatable resolves a dotted accessor by walking it, and the shared
// `sortRecords` comparator does the same, so a column and the sort agree only
// while they name the same path. `representative.name` is the zone's own field;
// `recordCount` and `delegation` are the GROUP's reductions and deliberately
// not the representative's, because a group can hold connections that disagree.

export const useZonesTableColumns = (
    /**
     * Connection id → the tenant's own name for it. Empty while connections
     * load, and an id that is missing falls back to the id's own tail rather
     * than rendering blank — an unnamed row is still more use than no way to
     * tell two rows apart.
     */
    connectionNames: ZoneConnectionNames = new Map(),
) => {

    // EVERY column is sortable (the module checklist is explicit), through the
    // shared comparator.
    const columns: DataTableColumn<GroupedZone>[] = [
        {
            accessor: 'representative.name',
            sortable: true,
            title: I18n.get('Name'),
            // FNP-1284 - NO WIDTH ON THE FLEXIBLE COLUMN. Leave it that way.
            //
            // A column with no width takes the space the fixed columns leave. Giving
            // this one a grid fraction (`2fr`) instead made it content-size: the
            // fixed columns are committed first, and the name column then grew to fit
            // its longest value, pushing the table wider than its container and
            // raising a horizontal scrollbar.
            //
            // `minWidth` in px is fine here if a floor is ever wanted. A grid
            // fraction is not: mantine-datatable puts the value in an inline style on
            // a real <table> cell, where `fr` is not a valid unit.
            render: (group: GroupedZone) => (
                // ⚠️ THE LINK USES THE REPRESENTATIVE'S OWN ids, and that is a
                // real decision rather than a convenience.
                //
                // `getDnsZone` takes BOTH a connectionId and a zone id, and a
                // Flexi zone id is only meaningful against the connection that
                // minted it. A group holds one id per connection, so the link
                // has to pick one — and it picks the FIRST in fetch order,
                // whole, rather than pairing one connection's id with another's
                // connectionId, which would resolve to nothing.
                //
                // ⚠️ WHICH CONNECTION A MUTATION USES WHEN SEVERAL CAN REACH A
                // ZONE IS EXPLICITLY OUT OF SCOPE on this ticket, and this link
                // does not settle it: it is a READ, and every connection that
                // reaches the zone reads the same zone. The write question is
                // the follow-on the ticket names.
                <NavLink
                    to={`${config.basePath}/${encodeURIComponent(group.representative.id)}?connectionId=${encodeURIComponent(group.representative.connectionId)}`}
                >
                    <Text size={'sm'}>{group.representative.name}</Text>
                </NavLink>
            ),
        },
        {
            accessor: 'representative.provider',
            sortable: true,
            title: I18n.get('Provider'),
            render: (group: GroupedZone) => (
                <Badge
                    variant={'light'}
                    color={providerColor(group.representative.provider)}
                >
                    {providerLabel(group.representative.provider)}
                </Badge>
            ),
        },
        // FNP-1378 — WHICH CONNECTIONS CAN REACH THIS ZONE.
        //
        // ⚠️ THIS IS WHAT KEEPS THE FAN-OUT VISIBLE AFTER GROUPING. Collapsing
        // nine rows into three is only honest if the three still say what the
        // nine said. The read path fans out across every connection and returns
        // the union — that is correct, because connections are normally
        // separate provider accounts — and this column is where that fact
        // survives into a grouped table.
        //
        // A tenant may connect the SAME account twice with different permission
        // levels: read-only for most work, a wider role for occasional changes.
        // `displayName` exists on a connection precisely because "a tenant with
        // a production and a staging Route 53 account has two connections that
        // are otherwise identical". When one zone is reachable both ways, this
        // column says so on the single row that zone now occupies.
        //
        // ⚠️ SORTED ON THE COUNT, not on the names. "How many ways can I reach
        // this zone" is the question an operator scans this column for, and it
        // is the one a sort can answer; sorting alphabetically on a joined
        // string would order by whichever connection happened to be read first.
        {
            accessor: 'connectionIds.length',
            sortable: true,
            title: I18n.get('Connections'),
            render: (group: GroupedZone) => (
                <ZoneConnections
                    connectionIds={group.connectionIds}
                    connectionNames={connectionNames}
                />
            ),
        },
        // ⚠️ THE GROUP'S REDUCTION, NOT THE REPRESENTATIVE'S FIELD. Two
        // connections reading one zone can reach different verdicts, and the
        // badge has a fourth state that says so rather than picking one.
        {
            accessor: 'delegation.kind',
            sortable: true,
            title: I18n.get('Delegation'),
            render: (group: GroupedZone) => (
                <GroupedDelegationBadge delegation={group.delegation}/>
            ),
        },
        {
            accessor: 'representative.isPrivate',
            sortable: true,
            title: I18n.get('Visibility'),
            // `isPrivate` is `Boolean!` — non-null, so unlike the hosted-zones
            // equivalent there is no third state to render here.
            render: (group: GroupedZone) => (
                <Badge
                    variant={'light'}
                    color={group.representative.isPrivate ? 'orange' : 'blue'}
                >
                    {group.representative.isPrivate
                        ? I18n.get('Private')
                        : I18n.get('Public')}
                </Badge>
            ),
        },
        {
            accessor: 'representative.nameservers',
            sortable: true,
            title: I18n.get('Nameservers'),
            // The COUNT, not the list: four hostnames per row would drown the
            // table. The full delegation set is on the detail page.
            //
            // ⚠️ AND AN EMPTY LIST RENDERS AS UNKNOWN, NOT `0` (FNP-1289) — the
            // list path never reads the delegation set, so zero here is an
            // absence of information, not an answer. See NameserverCount.
            //
            // ⚠️ NOT REDUCED ACROSS THE GROUP, because there is nothing to
            // reduce: `listZones` returns no nameservers for ANY connection
            // (Route 53's ListHostedZones does not carry the delegation set),
            // so every row of a group holds the same empty array. A reduction
            // here would be machinery for a disagreement that cannot occur on
            // this path.
            render: (group: GroupedZone) => (
                <NameserverCount zone={group.representative as DnsZone}/>
            ),
        },
        // FNP-1422 — HOW MANY RECORDS THE PROVIDER SAYS THIS ZONE HOLDS.
        //
        // ⚠️ UNLIKE THE NAMESERVER COLUMN, THIS ONE IS REAL ON THE LIST PATH.
        // Route 53 returns ResourceRecordSetCount on every entry of
        // ListHostedZones, so the count arrives with the zones at no extra
        // provider call — which is the only reason the column can exist. A
        // count fetched per row would be the N+1 the nameserver column refuses
        // to pay, on the module's first screen.
        //
        // ⚠️ AND BECAUSE IT IS REAL PER CONNECTION, IT CAN DISAGREE ACROSS ONE
        // (FNP-1378). A narrower role may not see every record set. The cell
        // shows both numbers in that case rather than picking one — see
        // GroupedRecordCountCell.
        {
            accessor: 'recordCount.kind',
            sortable: true,
            title: I18n.get('Records'),
            render: (group: GroupedZone) => (
                <GroupedRecordCountCell count={group.recordCount}/>
            ),
        },
        // ⚠️ THERE IS DELIBERATELY NO ZONE ID COLUMN.
        //
        // `DnsZone.id` is OPAQUE — Flexi's own identifier, minted by the
        // adapter, never the provider's. Putting it in front of an operator
        // invites them to paste it into the AWS console or a Cloudflare
        // dashboard, where it means nothing. The hosted-zones module shows its
        // zone id precisely because that one IS the Route 53 id.
        //
        // `providerZoneId` IS the provider's and IS shown — on the detail page
        // (FNP-1425), labelled with the provider's name so the two cannot be
        // confused. It is also the key this table groups on, which is a use
        // that never puts it on screen.
    ];

    return {
        columns,
    };
};
